import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { Button } from '@connected/ui/components/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/scope12/',
)({
    component: RouteComponent,
});

type CheckListItems = components['schemas']['ListElectricalCheckListItemQueryResultItem'];

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const { organizationId } = Route.useParams();

    const tabsKind = useMemo<RadioGroupOption<CheckListItems['Kind']>[]>(() => [
        {
            value: 'Scope12InspectionPlanDocument',
            label: t('dashboard.settings.scope2.tabs.scope12InspectionPlanDocument'),
        },
        {
            value: 'Scope12VisualInspection',
            label: t('dashboard.settings.scope2.tabs.scope12VisualInspection'),
        },
        {
            value: 'Scope12AdditionalInspectionPoints',
            label: t('dashboard.settings.scope2.tabs.scope12AdditionalInspectionPoints'),
        },
        {
            value: 'Scope12DocumentationDCSide',
            label: t('dashboard.settings.scope2.tabs.scope12DocumentationDCSide'),
        },
        {
            value: 'Scope12ManualAndDataSpecifications',
            label: t('dashboard.settings.scope2.tabs.scope12ManualAndDataSpecifications'),
        },
        {
            value: 'Scope12GeneralRemarks',
            label: t('dashboard.settings.scope2.tabs.scope12GeneralRemarks'),
        },
        {
            value: 'Scope12PVInstallationPowerLine',
            label: t('dashboard.settings.scope2.tabs.scope12PVInstallationPowerLine'),
        },
        {
            value: 'Scope12InspectionExecution',
            label: t('dashboard.settings.scope2.tabs.scope12InspectionExecution'),
        },
        {
            value: 'Scope12DocumentationACSide',
            label: t('dashboard.settings.scope2.tabs.scope12DocumentationACSide'),
        },
        {
            value: 'Scope12MeasurementsAndTesting',
            label: t('dashboard.settings.scope2.tabs.scope12MeasurementsAndTesting'),
        },
        {
            value: 'Scope12VisualInspectionOtherParts',
            label: t('dashboard.settings.scope2.tabs.scope12VisualInspectionOtherParts'),
        },
        {
            value: 'Scope12RoofConstructionCalculation',
            label: t('dashboard.settings.scope2.tabs.scope12RoofConstructionCalculation'),
        },
    ], [t]);

    const columns = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: RadioGroupOption<CheckListItems['Kind']> } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.label}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: RadioGroupOption<CheckListItems['Kind']> } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex underline font-semibold gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                        >
                            <Link
                                to="/dashboard/organizations/$organizationId/settings/scope12/$scopeName"
                                params={{
                                    organizationId,
                                    scopeName: row?.original?.value?.toLowerCase() ?? '',
                                }}
                            >
                                {t('dashboard.overview')}
                            </Link>
                        </Button>
                    </div>
                );
            },
        },
    ], [organizationId, t]);

    const tableData = useMemo<RadioGroupOption<CheckListItems['Kind']>[]>(
        () => {
            return (
                tabsKind
                    ?.filter((norm) => {
                        return [
                            norm.label || '',
                        ].some(field => field?.toString().toLowerCase());
                    })
                    .map(norm => norm)
                    || []
            );
        },
        [tabsKind],
    );

    return (
        <div>
            <div className="w-full">
                <DataTable.TableHead
                    title={t('dashboard.settings.scope12.title')}
                    subtitle={t('dashboard.settings.scope12.subtitle')}
                />
            </div>
            <DataTable
                className="rounded-tl-none rounded-tr-none"
                columns={columns}
                data={tableData}
                pageSize={12}
            />
        </div>
    );
}
